import { LOAN_STAGES } from '@/types'
import type { Loan, LoanStage } from '@/types'

export type ViewMode = 'table' | 'funnel' | 'connections'
export type InspectorTab =
  | 'overview'
  | 'economics'
  | 'blockers'
  | 'activity'
  | 'connections'
  | 'lineage'
  | 'audit'

/** Quick-log actions on the Deal Inspector footer. */
export type InspectorLogKind = 'call' | 'email' | 'meeting' | 'note' | 'task'
export type MetricFilter = 'all' | 'funded' | 'exceptions' | 'missing' | 'sla' | 'needsAddress'

export type ColumnId =
  | 'id'
  | 'property'
  | 'stage'
  | 'loanAmount'
  | 'fullyFunded'
  | 'initialAdvance'
  | 'futureFunding'
  | 'location'
  | 'type'
  | 'product'
  | 'sponsor'
  // Retired when Sponsor and Borrower merged. Kept in the union so a saved
  // view or column preset from before the merge still parses; COLUMN_MAP no
  // longer has an entry for it, and unknown ids are already skipped.
  | 'borrower'
  | 'assignee'
  | 'underwriter'
  // Gate 0 F1. A grid column and a filter, not a Backshop report column.
  | 'secondaryUnderwriter'
  | 'sla'
  | 'ltv'
  | 'dscr'
  | 'debtYield'
  | 'daysOpen'
  | 'daysInStatus'
  | 'originated'
  // The deal's last change (R-12, 29 Oct 2026): the record's updated_at.
  | 'lastModified'
  | 'priority'
  | 'completeness'
  | 'exceptions'
  | 'broker'
  // Backshop pipeline columns (Report Studio / Columns panel / saved views).
  | 'city'
  | 'country'
  | 'units'
  | 'sf'
  | 'purpose'
  | 'ltc'
  | 'ltvStabilized'
  | 'debtYieldStabilized'
  | 'termMonths'
  | 'extensions'
  | 'ioMonths'
  | 'amortTerm'
  | 'amortizationType'
  | 'rateType'
  | 'spread'
  | 'coupon'
  | 'brokerage'
  | 'brokerContact'
  | 'received'
  | 'killedAt'
  | 'killReason'
  | 'killedAtStage'
  // Backshop's combined cells: Units with its measure, "LTV / LTC", and Amort Term or IO.
  | 'unitsMeasured'
  | 'ltvLtc'
  | 'amortOrIo'
  // Current Status report columns (call #18/#19). Gap columns always print "—".
  | 'fundedAmount'
  | 'dealStatus'
  | 'currentStatusDate'
  | 'currentStatusReason'
  | 'currentStatusComments'
  | 'previousLoanStatus'
  | 'previousNote'
  | 'previousManager'
  | 'originator'
  | 'analyst'
  | 'closer'
  // The quote and closing clocks (punch list CY-08/09/10).
  | 'quoteLetterDate'
  | 'quoteAge'
  | 'depositWireDate'
  | 'closeByDate'
  | 'daysToCloseBy'

/**
 * "Which deals were just added or changed" (R-12): the deal entered the
 * pipeline (Date added) or last changed (Last modified) today, in the last 7
 * days or in the last 30, on the desk's calendar.
 */
export type RecencyWindow = 'today' | '7d' | '30d'
export type RecencyFilter = `${'added' | 'modified'}:${RecencyWindow}`

export type AggMethod = 'none' | 'sum' | 'avg' | 'count' | 'distinct' | 'min' | 'max'

/** Per-column filter. Which fields apply depends on the column kind. */
export type ColumnFilterValue = {
  /** enum columns: keep rows whose display value is in this set (empty = all) */
  values?: string[]
  /** text columns: case-insensitive substring match */
  contains?: string
  /** number columns: inclusive range */
  min?: number | null
  max?: number | null
  /** date columns: inclusive ISO date range */
  from?: string
  to?: string
}

/**
 * The paper a designed pipeline report prints on. All are landscape — a pipeline table is wider
 * than it is tall. Legal is the default because it is what every pipeline print was before the
 * choice existed, so a report that never named a paper prints exactly as it did.
 *
 * Mirrors `PAPER_SIZES` in backend/app/reporting/report_config.py.
 */
export type PaperSize = 'letter' | 'legal' | 'tabloid'

export const PAPER_SIZES: ReadonlyArray<{
  id: PaperSize
  label: string
  /** Landscape, width × height, in inches. */
  dimensions: string
  /** Landscape width in inches — the number the PDF column cap is computed from. */
  widthIn: number
  /** Landscape height in inches. */
  heightIn: number
  /** Excel's paper code (ExcelJS `paperSize`): 1 = Letter, 5 = Legal, 3 = Tabloid. */
  excelCode: number
}> = [
  { id: 'letter', label: 'Letter', dimensions: '11 × 8.5 in', widthIn: 11, heightIn: 8.5, excelCode: 1 },
  { id: 'legal', label: 'Legal', dimensions: '14 × 8.5 in', widthIn: 14, heightIn: 8.5, excelCode: 5 },
  { id: 'tabloid', label: 'Tabloid', dimensions: '17 × 11 in', widthIn: 17, heightIn: 11, excelCode: 3 },
]

export const DEFAULT_PAPER_SIZE: PaperSize = 'legal'

/** Side margins the PDF table already subtracts (`builder._view_grid_pdf_flow`). */
export const PDF_SIDE_MARGIN_IN = 1.1
/** Minimum readable body size used to count how many PDF columns fit. */
export const MIN_PDF_COLUMN_FONT_PT = 6
/** Helvetica average glyph ≈ 0.5em; ~9 characters plus padding. */
export const MIN_PDF_COLUMN_PT = MIN_PDF_COLUMN_FONT_PT * 0.5 * 9 + 8

export function defaultMaxPdfColumns(paperSize: PaperSize): number {
  const paper = PAPER_SIZES.find((option) => option.id === paperSize) ?? PAPER_SIZES[1]
  const availPt = (paper.widthIn - PDF_SIDE_MARGIN_IN) * 72
  return Math.max(1, Math.floor(availPt / MIN_PDF_COLUMN_PT))
}

/** Resolved per-paper limits from the server (`PDF_MAX_COLUMNS_*` when set). */
export type PdfColumnCaps = Partial<Record<PaperSize, number>>

export function maxPdfColumns(paperSize: PaperSize, configured?: PdfColumnCaps | null): number {
  const override = configured?.[paperSize]
  if (typeof override === 'number' && Number.isFinite(override) && override >= 1) {
    return Math.floor(override)
  }
  return defaultMaxPdfColumns(paperSize)
}

export function pdfColumnCapMessage(
  paperSize: PaperSize,
  columnCount: number,
  configured?: PdfColumnCaps | null,
): string {
  const paper = PAPER_SIZES.find((option) => option.id === paperSize) ?? PAPER_SIZES[1]
  const limit = maxPdfColumns(paperSize, configured)
  return (
    `PDF and Print are limited to ${limit} columns on ${paper.label}. ` +
    `This design has ${columnCount}. Remove columns or switch to a wider paper. ` +
    'Excel is still available.'
  )
}

export function isPaperSize(value: unknown): value is PaperSize {
  return value === 'letter' || value === 'legal' || value === 'tabloid'
}

export type ReportStyle = {
  headerBg: string
  headerText: string
  rowBg: string
  rowAltBg: string
  rowText: string
  zebra: boolean
  wrapText: boolean
  highlightExceptions: boolean
}

/**
 * Report designer state. Persisted inside the saved view's `filters` JSON
 * (`filters.report`) so versioning, restore, branch, and schedules capture it
 * without a backend migration.
 */
export type ReportConfig = {
  /**
   * Library folder this report files under. Carried in the view's `filters`
   * JSON rather than a dedicated column, so folders persist through the
   * existing create/update/version endpoints with no schema migration.
   * Empty string means unfiled.
   */
  folder: string
  title: string
  subtitle: string
  /**
   * How XLSX/CSV artifacts are themed. Spreadsheets are print-and-share
   * artifacts, so they default to the light print palette even when the
   * on-screen preview is dark; 'match' opts into the preview's exact colors.
   */
  exportTheme: 'print' | 'match'
  columnFilters: Partial<Record<ColumnId, ColumnFilterValue>>
  dateFrom: string
  dateTo: string
  groupBy: ColumnId | ''
  sortId: ColumnId
  sortDir: 'asc' | 'desc'
  style: ReportStyle
  showTotals: boolean
  totals: Partial<Record<ColumnId, AggMethod>>
  /** The paper the PDF and the workbook print on. Absent in a design saved before the choice existed. */
  paperSize: PaperSize
}

/**
 * Which deals a saved view reads: the live pipeline, the dead deals kept for
 * comparison, or both. The vocabulary the Deal Locator already uses for its
 * status filter.
 */
export type DealScope = 'active' | 'dead' | 'all'

export type FilterState = {
  search: string
  stage: string
  assetClass: string
  assignee: string
  /**
   * Gate 0 F1: keep the deals that name this person as their Secondary
   * Underwriter. Absent (or 'All') is no filter, so every view saved before this
   * key existed reads exactly as it did. The server's pipeline reports read the
   * same key (`builder._pipeline_loans`).
   */
  secondaryUnderwriter?: string
  /**
   * Keep the deals that name this person as their Analyst (`custom_fields.analyst`),
   * or, with `NO_ANALYST`, the deals that name none (T-57, 2 Nov 2026: "each one is
   * supposed to have an analyst assigned to it"). Absent or 'All' is no filter. The
   * server's pipeline reports read the same key (`builder._pipeline_loans`).
   */
  analyst?: string
  year: string
  metric: MetricFilter
  /**
   * R-12: keep the deals added or changed within a recent window. Absent is no
   * filter, so every view saved before this key existed reads as it did. The
   * grid applies it; the server's pipeline reports do not read it yet.
   */
  recency?: RecencyFilter
  report?: ReportConfig
  /**
   * The deal-status scope of the view. Absent means 'active' — every view saved
   * before this key existed reads the live pipeline exactly as it did. Stored in
   * the view's `filters` JSON, so no migration; the server's pipeline reports
   * read the same key (`builder._pipeline_loans`).
   */
  dealStatus?: DealScope
  /**
   * A kill-reason code ('parallel_run_reset') that narrows WHICH dead deals the scope reads: the
   * comparison views show the deals the 24 Sep reset killed, not every dead deal. A live deal is
   * never touched by it, a dead deal with another (or no) recorded reason is not shown, and it
   * does nothing in an 'active' scope. Absent means every dead deal. Stored in the view's
   * `filters` JSON; the server's reports read the same key (`builder._pipeline_loans`).
   */
  killReason?: string
}

export type ReportVersion = {
  id?: string
  version: number
  createdAt: string | null
  authorEmail: string
  note: string
  columns: ColumnId[]
  filters: FilterState
}

export type ViewAclUpdate = {
  isShared?: boolean
  editors?: string[]
  viewers?: string[]
}

export type ScheduleFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly'
/**
 * What a schedule produces.
 *
 * `pdf` and `xlsx` name a file. `digest` does not: the artifact is still the
 * PDF, retained and checksummed like any other, and `digest` says only that the
 * mail carrying it is the day’s pipeline written into the message rather than a
 * standing notice with a link. Only a template that has a digest to render may
 * ask for one, which is why it is declared per template below.
 */
export type ScheduleFormat = 'pdf' | 'xlsx' | 'digest'
export type ScheduleTemplate = 'pipeline_summary' | 'pipeline_daily' | 'current_status'
export type ScheduleTriggerType = 'time' | 'event'
export type ScheduleDeliveryMode = 'none' | 'reviewer' | 'team'

/** Every template the report engine can render (backend/app/reporting/builder.py
 *  TEMPLATES + XLSX_TEMPLATES) — the full set scheduling covers, not just the
 *  two saved-view-scoped pipeline templates above. */
export const REPORT_TEMPLATES = [
  { id: 'pipeline_summary', label: 'Pipeline Summary', formats: ['pdf', 'xlsx'] as ScheduleFormat[], scoped: false },
  { id: 'pipeline_backshop', label: 'Pipeline Report (Backshop layout)', formats: ['pdf', 'xlsx'] as ScheduleFormat[], scoped: false },
  { id: 'current_status', label: 'Current Status', formats: ['pdf', 'xlsx'] as ScheduleFormat[], scoped: false },
  { id: 'pipeline_changes', label: 'Pipeline Changes', formats: ['pdf', 'xlsx'] as ScheduleFormat[], scoped: false },
  { id: 'pipeline_daily', label: 'Daily Pipeline (change bar)', formats: ['pdf', 'digest'] as ScheduleFormat[], scoped: false },
  { id: 'portfolio', label: 'Portfolio Report', formats: ['pdf'] as ScheduleFormat[], scoped: false },
  { id: 'surveillance', label: 'Surveillance Report', formats: ['pdf'] as ScheduleFormat[], scoped: false },
  { id: 'term_sheet', label: 'Indicative Term Sheet', formats: ['pdf'] as ScheduleFormat[], scoped: true },
  { id: 'deal_package', label: 'Deal Package', formats: ['pdf'] as ScheduleFormat[], scoped: true },
  { id: 'deal_template', label: 'Deal Excel Workbook', formats: ['xlsx'] as ScheduleFormat[], scoped: true },
  { id: 'committee_book', label: 'Committee Book', formats: ['pdf'] as ScheduleFormat[], scoped: false },
  { id: 'screener', label: 'Screener', formats: ['pdf'] as ScheduleFormat[], scoped: false },
  { id: 'asset_management', label: 'Asset Management Report', formats: ['pdf'] as ScheduleFormat[], scoped: false },
] as const

export type ReportTemplateId = (typeof REPORT_TEMPLATES)[number]['id']

/** True when the template renders one specific loan and needs filters.loan_id. */
export function templateNeedsLoan(template: string): boolean {
  return REPORT_TEMPLATES.find((t) => t.id === template)?.scoped ?? false
}

export const SCHEDULE_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
]

/** A schedule against the general report engine (any REPORT_TEMPLATES id) —
 *  distinct from ViewSchedule, which is always scoped to one saved pipeline
 *  view via /pipeline-views/{id}/schedules. This hits /reports/scheduled. */
export type ReportSchedule = {
  id: string
  name: string
  template: string
  frequency: ScheduleFrequency | string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  hourUtc: number
  /** Minute past the hour, read in `timezone` alongside hourUtc. */
  minuteLocal: number
  timezone: string
  businessDaysOnly: boolean
  triggerType: ScheduleTriggerType | string
  triggerEventType?: string | null
  recipients: string[]
  filters: Record<string, unknown>
  format: ScheduleFormat | string
  deliveryMode: ScheduleDeliveryMode | string
  senderEmail?: string | null
  emailSubject?: string | null
  emailPreheader?: string | null
  emailBody?: string | null
  isActive: boolean
  /** The saved pipeline report this schedule runs, when it has one. */
  pipelineViewId?: string | null
  /** True only when a due run would email its recipients right now. */
  willEmail?: boolean
  /** Every gate that would stop that email; empty when `willEmail`. */
  deliveryBlockers?: ScheduleDeliveryBlocker[]
  /** Per named recipient: exactly on the approved list or not. The list itself is never served. */
  recipientApproval?: Record<string, boolean>
  /** Who saved the schedule. */
  createdBy?: string | null
  lastRunAt?: string | null
  lastRunStatus?: string | null
  nextRunAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export type ReportScheduleInput = {
  name: string
  template: string
  frequency: ScheduleFrequency | string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  hourUtc: number
  minuteLocal?: number
  timezone?: string
  businessDaysOnly?: boolean
  triggerType?: ScheduleTriggerType
  triggerEventType?: string | null
  recipients: string[]
  filters?: Record<string, unknown>
  format: ScheduleFormat
  deliveryMode?: ScheduleDeliveryMode
  senderEmail?: string | null
  emailSubject?: string | null
  emailPreheader?: string | null
  emailBody?: string | null
  isActive?: boolean
}

export type ReportEmailDefaults = {
  senderEmail: string | null
  senderAliases?: string[]
  subject: string
  preheader: string
  body: string
  tokens: Record<string, string>
  brandReference: string
}

export type ReportEmailPreview = {
  senderEmail: string | null
  recipient: string
  subject: string
  preheader: string
  bodyText: string
  html: string
  attachmentName: string
  sendMode: 'preview_only' | string
}

export type ReportDeliveryStatus =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'failed'
  | 'sending'
  | 'retained'
  | string

export type ReportDeliveryHistoryItem = {
  scheduleId: string
  scheduleName: string
  runId: string
  attemptId: string | null
  reportId: string | null
  reportTitle: string | null
  recipient: string | null
  status: ReportDeliveryStatus
  sentAt: string | null
  deliveredAt: string | null
  openedAt: string | null
  openCount: number
  providerMessageId: string | null
  lastEventAt?: string | null
  error: string | null
  completedAt: string | null
  /** False when the schedule that made this run has since been deleted. Absent from an older server. */
  scheduleExists?: boolean
  /** The run's own outcome (completed, failed, ...), apart from any email attempt. */
  runStatus?: string | null
  /** time or event. */
  runTrigger?: string | null
  /** The fire time the run was claimed for. */
  scheduledFor?: string | null
  /** The generated file's format, and whether the archive still holds it. */
  reportFormat?: string | null
  reportDownloadable?: boolean
}

export type ReportDeliveryHistory = {
  capabilities: {
    smtpAcceptance: boolean
    providerDeliveryReceipts: boolean
    openTracking: boolean
  }
  summary: {
    total: number
    sent: number
    delivered: number
    opened: number
    failed: number
    sending: number
    retained: number
  }
  items: ReportDeliveryHistoryItem[]
}

/** One gate standing between a due run and an email (fixed code, plain label, the setting that governs it). */
export type ScheduleDeliveryBlocker = { code: string; label: string; setting: string | null }

export type ViewSchedule = {
  id: string
  name: string
  template: ScheduleTemplate | string
  frequency: ScheduleFrequency | string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  /** The hour on the clock of `timezone` (the field name predates timezones). */
  hourUtc: number
  /** Minute past that hour, on the same clock. */
  minuteLocal?: number
  /** True only when a due run would email its recipients right now. */
  willEmail?: boolean
  /** Every gate that would stop that email; empty when `willEmail`. */
  deliveryBlockers?: ScheduleDeliveryBlocker[]
  timezone: string
  businessDaysOnly: boolean
  triggerType: ScheduleTriggerType | string
  triggerEventType?: string | null
  recipients: string[]
  format: ScheduleFormat | string
  deliveryMode: ScheduleDeliveryMode | string
  senderEmail?: string | null
  emailSubject?: string | null
  emailPreheader?: string | null
  emailBody?: string | null
  isActive: boolean
  pinVersion?: number | null
  lastRunAt?: string | null
  lastRunStatus?: string | null
  nextRunAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

export type ViewScheduleInput = {
  name: string
  template?: ScheduleTemplate
  frequency: ScheduleFrequency | string
  dayOfWeek?: number | null
  dayOfMonth?: number | null
  hourUtc: number
  minuteLocal?: number
  timezone?: string
  businessDaysOnly?: boolean
  triggerType?: ScheduleTriggerType
  triggerEventType?: string | null
  recipients: string[]
  format: ScheduleFormat
  deliveryMode?: ScheduleDeliveryMode
  senderEmail?: string | null
  emailSubject?: string | null
  emailPreheader?: string | null
  emailBody?: string | null
  isActive?: boolean
  pinVersion?: number | null
}

export type SavedView = {
  id: string
  name: string
  description: string
  status: 'Published' | 'Draft' | 'published' | 'draft'
  ownerEmail: string
  version: number
  updatedAt: string | null
  columns: ColumnId[]
  filters: FilterState
  versions: ReportVersion[]
  isSystem?: boolean
  isShared?: boolean
  editors?: string[]
  viewers?: string[]
  /** Client-only fallback when the saved-view service is unreachable. */
  isLocal?: boolean
}

export type ColumnGroup = 'Identity' | 'Structure' | 'Credit' | 'Workflow' | 'Governance'

export type LoanProvenance = {
  loanId: string
  linked: boolean
  sourceIngestionId: string | null
  package: {
    sourceType?: string
    sourceEmail?: string | null
    sourceSubject?: string | null
    receivedAt?: string | null
    extractionConfidence?: number | null
    extractionNotes?: string | null
    status?: string | null
    attachments?: unknown[]
  } | null
  fields: Array<{
    field: string
    value: unknown
    source: string
    confidence: number | null
    state: string
    downstream: string[]
  }>
  message: string | null
}

export type LoanActivityEvent = {
  id: number
  eventType: string
  userEmail: string | null
  createdAt: string | null
  details: Record<string, unknown>
  route?: string | null
  method?: string | null
}

/** Raw engine value states the event trail stores — never invent states
 *  client-side. The display vocabulary (the four agreed value states plus the
 *  absences) comes from the lineage row's own `state`, read through
 *  `stateOfLineage` in components/fields/lineage. */
export type FieldValueState =
  | 'imported'
  | 'extracted'
  | 'inferred'
  | 'suggested'
  | 'conflicting'
  | 'missing'
  | 'manual'
  | 'approved'
  | 'overridden'
  | 'recorded'
  | 'not_applicable'

/** The agreed display state of a figure (Derived-Value Conflict Rule v1.0). */
export type AgreedValueState =
  | 'stated'
  | 'derived'
  | 'suggested'
  | 'confirmed'
  | 'conflicted'
  | 'missing'
  | 'not_applicable'

/** Whose statement a stated value is. `sponsor` only when the sponsor's own
 *  underwriting model is the source. */
export type StatedAttribution = 'sponsor' | 'broker' | 'external' | 'client' | 'unattributed'

/** Stated: the document, the place in it, and the date it said so. */
export type LineageProvenance = {
  document: string | null
  sourceSha256?: string | null
  ingestionId?: string | null
  page?: string | null
  cellRef?: string | null
  lineNo?: string | number | null
  method?: string | null
  raw?: string | null
  statedAt?: string | null
  observedAt?: string | null
  sender?: string | null
  note?: string | null
  inheritedFrom?: string | null
  attribution?: StatedAttribution | null
  attributionWhy?: string | null
}

/** Derived: the formula, the input values, the engine build (rule R2). */
export type LineageDerivation = {
  formula: string | null
  /** `source`: where a READ input came from — a document, or the file, sheet and
   *  cell of a sponsor model. Absent for an input with no recorded place. */
  inputs: { field: string; label: string; value: unknown; source?: string | null }[]
  /** False when the event predates recorded inputs — the values shown are
   *  the inputs as they stand now. */
  inputsRecorded: boolean
  engineVersion: string | null
  computedAt: string | null
  computedBy?: string | null
  note?: string | null
}

/** Suggested: why the platform offers this value. */
export type LineageSuggestion = { basis: string; value?: unknown; source?: string | null }

/** Confirmed: who, when and why. */
export type LineageConfirmation = {
  by: string | null
  at: string | null
  rationale: string | null
  via?: string | null
  kind?: string | null
}

/** A stated figure the deal's own arithmetic does not reproduce (rule R4) —
 *  for information, never a choice between outputs. */
export type LineageVariance = {
  stated: unknown
  computed: unknown
  formula: string | null
  informational?: boolean
}

export type StatedFigureStatus = 'not_stated' | 'no_calculation' | 'withheld' | 'agrees' | 'variance'

/** One row of `statedFigures` on `GET /loans/{id}/field-lineage`: a figure a
 *  broker stated, kept as stated, beside the platform's own calculation of it. */
export type StatedFigure = {
  /** The broker-stated field, e.g. `cap_rate_stated`. */
  field: string
  /** The figure it keeps, e.g. `cap_rate`. */
  base: string
  label: string
  unit: string | null
  stated: number | null
  calculated: number | null
  formula: string | null
  /** Stated less calculated, in the figure's own unit (points for a rate). */
  delta: number | null
  /** That gap as a percentage of the calculated figure. */
  deltaPct: number | null
  status: StatedFigureStatus
  note: string | null
}

export type LoanFieldHistoryEvent = {
  id: string
  loanId?: string
  fieldName: string
  oldValue: unknown
  newValue: unknown
  valueState: FieldValueState | string
  /** The state word as stored. `valueState` reads a machine backfill row
   *  (`approved` under system:reconciliation) as `extracted`, never as a
   *  person's confirmation (dossierui-12). */
  storedState?: string | null
  source: string | null
  confidence: number | null
  reason: string | null
  editorEmail: string | null
  editorUserId?: string | null
  createdAt: string | null
  /** Rule R2 — the inputs a derived value was computed from. */
  inputs?: { field: string; label: string; value: unknown }[] | null
  engineVersion?: string | null
}

/** The desk-ownership stamp on a covenant term (`field_lineage.desk_ownership`). */
export type LineageOwnership = {
  owner: 'desk'
  seed: 'sponsor' | 'desk' | null
  /** "Sponsor-provided, initial" or "Desk-set"; null when no source can be told. */
  stamp: string | null
  /** True while the value is the sponsor's initial reading and the desk has not set its own. */
  awaitingDeskTerms: boolean
  note: string | null
}

export type LoanFieldLineageField = {
  fieldName: string
  /** Present when API uses legacy `field` key before camel normalize. */
  field?: string
  currentValue: unknown
  value?: unknown
  valueState: FieldValueState | string
  /** The agreed display state — exactly one per figure. */
  state?: AgreedValueState | string
  /** One line a reader can check: file · page · cell · date, the formula,
   *  the basis, or who confirmed it. Never "Not recorded". */
  summary?: string | null
  provenance?: LineageProvenance | null
  derivation?: LineageDerivation | null
  suggestion?: LineageSuggestion | null
  confirmation?: LineageConfirmation | null
  variance?: LineageVariance | null
  /** Whose word a covenant term is: the sponsor's initial reading until the
   *  desk sets it (Chris Young, 26 Oct 2026), then the desk's. Only the
   *  sponsor-seeded desk fields carry one; null everywhere else. */
  ownership?: LineageOwnership | null
  /** Empty, and an attached model the engine has not read may state it —
   *  with the sheet!cell its survey found (values-12). */
  unreadModels?: string[] | null
  modelCell?: string | null
  /** Empty, and an attached file the engine could not read may state it
   *  (dossierui-13). */
  unreadDocuments?: string[] | null
  source: string | null
  confidence: number | null
  reason?: string | null
  editorEmail: string | null
  editorUserId?: string | null
  updatedAt: string | null
  createdAt?: string | null
  history: LoanFieldHistoryEvent[]
}

export type LoanFieldLineageResponse = {
  loanId: string
  linked: boolean
  sourceIngestionId: string | null
  package: LoanProvenance['package']
  fields: LoanFieldLineageField[]
  /** The engine's stated-vs-derived variances; the Audit count is this list. */
  variances?: { field: string; stated: unknown; computed: unknown; formula: string | null }[]
  /** Each broker-stated figure beside the platform's own calculation and the
   *  variance between them (Gate 0 D1). Not part of `variances`: a stated row is
   *  the same disagreement the base figure already reports. */
  statedFigures?: StatedFigure[]
  message: string | null
}

export const STAGES: LoanStage[] = [...LOAN_STAGES]

export const DEFAULT_COLUMNS: ColumnId[] = [
  'property',
  'stage',
  'loanAmount',
  'fullyFunded',
  'type',
  'location',
  // Independent size facts (Edward, 18 Oct 2026): a unit/keys/beds count and
  // building area are two columns. Backshop's 26-column report still prints
  // the derived Units + UOM pair (`unitsMeasured`).
  'units',
  'sf',
  // One counterparty column. Sponsor and borrower were merged into a single
  // field, so the grid shows it once, under 'sponsor'.
  'sponsor',
  'assignee',
  'ltv',
  'dscr',
  // E5: the default aging column is per-stage dwell, not cumulative age.
  // Total Age ('daysOpen') stays available but is no longer the default.
  // The owner SLA sits beside it. This is the WORKING set (backend
  // pipeline_views.E5_WORKING_COLUMNS, held to it by
  // pipelineDefaultColumns.parity.test.ts) — not the Active Pipeline default: that
  // view is the Backshop report's layout, which the server serves as data.
  'daysInStatus',
  'sla',
  'completeness',
  'exceptions',
]

export const EMPTY_FILTERS: FilterState = {
  search: '',
  stage: 'All',
  assetClass: 'All',
  assignee: 'All',
  year: 'All',
  metric: 'all',
}

/** Matches the workspace grid's rendered palette so the default preview is faithful. */
export const REPORT_STYLE_PRESETS: Record<'Studio dark' | 'Print light' | 'Gold ledger', ReportStyle> = {
  'Studio dark': {
    headerBg: '#121212',
    headerText: '#a8a29e',
    rowBg: '#0c0c0c',
    rowAltBg: '#111111',
    rowText: '#d6d3d1',
    zebra: false,
    wrapText: false,
    highlightExceptions: false,
  },
  'Print light': {
    headerBg: '#1f2937',
    headerText: '#ffffff',
    rowBg: '#ffffff',
    rowAltBg: '#f3f4f6',
    rowText: '#111827',
    zebra: true,
    wrapText: false,
    highlightExceptions: false,
  },
  'Gold ledger': {
    headerBg: '#221c10',
    headerText: '#e7c778',
    rowBg: '#0c0c0c',
    rowAltBg: '#14110a',
    rowText: '#d6d3d1',
    zebra: true,
    wrapText: false,
    highlightExceptions: true,
  },
}

export const DEFAULT_REPORT_CONFIG: ReportConfig = {
  folder: '',
  title: '',
  subtitle: '',
  exportTheme: 'print',
  columnFilters: {},
  dateFrom: '',
  dateTo: '',
  groupBy: '',
  sortId: 'loanAmount',
  sortDir: 'desc',
  style: { ...REPORT_STYLE_PRESETS['Studio dark'] },
  showTotals: true,
  totals: {
    property: 'count',
    loanAmount: 'sum',
    fullyFunded: 'sum',
    initialAdvance: 'sum',
    futureFunding: 'sum',
    ltv: 'avg',
    dscr: 'avg',
    debtYield: 'avg',
    daysOpen: 'avg',
    daysInStatus: 'avg',
    completeness: 'avg',
    exceptions: 'sum',
  },
  paperSize: DEFAULT_PAPER_SIZE,
}

/**
 * Deep-ish clone with defaults filled in for configs saved by older versions.
 * `style` is accepted partially because persisted JSON predates later keys.
 */
export function normalizeReportConfig(
  raw?: (Partial<Omit<ReportConfig, 'style'>> & { style?: Partial<ReportStyle> }) | null,
): ReportConfig {
  return {
    ...DEFAULT_REPORT_CONFIG,
    ...(raw ?? {}),
    columnFilters: { ...(raw?.columnFilters ?? {}) },
    style: { ...DEFAULT_REPORT_CONFIG.style, ...(raw?.style ?? {}) },
    totals: { ...(raw?.totals ?? DEFAULT_REPORT_CONFIG.totals) },
    // A stored value this build does not print is the default paper, never an error.
    paperSize: isPaperSize(raw?.paperSize) ? raw.paperSize : DEFAULT_PAPER_SIZE,
  }
}

export type EditableLoanPatch = Partial<
  Pick<
    Loan,
    | 'property'
    | 'borrower'
    | 'sponsor'
    | 'location'
    | 'type'
    | 'assignee'
    | 'ltv'
    | 'dscr'
    | 'loanAmount'
    | 'originated'
    | 'rate'
    | 'priority'
    | 'brokerName'
  >
>
